"use client";

import React from "react";
import { Calendar, User, ArrowLeft, Tag, Share2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div data-section-id="37103" className="mx-auto max-w-4xl px-4 py-12">
        <div className="mb-8 space-y-4">
          <div className="h-4 w-24 animate-pulse rounded-sm bg-muted" />
          <div className="h-12 w-3/4 animate-pulse rounded-sm bg-muted" />
          <div className="flex gap-4">
            <div className="h-4 w-32 animate-pulse rounded-sm bg-muted" />
            <div className="h-4 w-32 animate-pulse rounded-sm bg-muted" />
          </div>
        </div>
        <div className="h-96 w-full animate-pulse rounded-sm bg-muted" />
        <div className="mt-8 space-y-4">
          <div className="h-4 w-full animate-pulse rounded-sm bg-muted" />
          <div className="h-4 w-full animate-pulse rounded-sm bg-muted" />
          <div className="h-4 w-2/3 animate-pulse rounded-sm bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <p className="mb-4 text-muted-foreground">Unable to load project details.</p>
        <Button onClick={refetch} variant="outline" className="rounded-sm">
          Retry
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <p className="text-muted-foreground">Project details not found.</p>
        <Link to="#" className="mt-4">
          <Button className="rounded-sm">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const date = post.date ? new Date(post.date).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }) : "";

  
  const categories = post.categories?.map((cat) => cat.name).join(", ") || "General Tiling";

  return (
    <article className="animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="bg-accent/40 border-b border-border/40">
        <div className="mx-auto max-w-4xl px-4 py-12 md:py-16">
          <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="#" className="hover:text-secondary transition-colors">
              Home
            </Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="#" className="hover:text-secondary transition-colors">
              Expertise
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground font-medium truncate max-w-[200px]">
              {post.title}
            </span>
          </div>

          <h1 
            className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:leading-[1.1] font-sans"
            dangerouslySetInnerHTML={{ __html: post.title }}
          />

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8 border border-border">
                <AvatarImage src={post.author?.avatar?.["96"]} alt={post.author?.name} />
                <AvatarFallback>
                  <User className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">
                {post.author?.name || "SimProTiling Expert"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              <span className="text-secondary font-semibold">{categories}</span>
            </div>
          </div>
        </div>
      </div>

      {post.featuredImage && (
        <div className="mx-auto max-w-5xl px-4 -mt-8 md:-mt-12 relative z-10">
          <div className="overflow-hidden rounded-sm border border-border bg-card shadow-md">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-auto w-full object-cover max-h-[600px]"
            />
          </div>
        </div>
      )}

      <div className="mx-auto max-w-4xl px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_250px]">
          
          {}
          <div className="min-w-0">
            <div 
              className="prose prose-lg prose-neutral dark:prose-invert max-w-none
                prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-foreground
                prose-p:leading-7 prose-p:text-muted-foreground
                prose-a:text-secondary prose-a:no-underline hover:prose-a:underline
                prose-strong:font-semibold prose-strong:text-foreground
                prose-ul:list-disc prose-ul:pl-6
                prose-img:rounded-sm prose-img:border prose-img:border-border"
              dangerouslySetInnerHTML={{ __html: post.content || "" }}
            />

            {}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-border">
                <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Service Expertise
                </h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, i) => (
                    <span 
                      key={tag.id} 
                      data-index={i}
                      className="inline-flex items-center rounded-sm bg-muted px-3 py-1 text-sm font-medium text-foreground hover:bg-secondary/10 hover:text-secondary transition-colors cursor-default"
                    >
                      #{tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {}
          <aside className="space-y-8">
            <Card className="sticky top-24 overflow-hidden border-border shadow-sm rounded-sm">
              <div className="bg-secondary/5 p-6 text-center">
                <Avatar className="mx-auto h-20 w-20 border-4 border-background shadow-sm">
                  <AvatarImage src={post.author?.avatar?.["96"]} alt={post.author?.name} />
                  <AvatarFallback className="bg-secondary/10 text-secondary">
                    <User className="h-8 w-8" />
                  </AvatarFallback>
                </Avatar>
                <h3 className="mt-4 text-lg font-bold text-foreground">
                  {post.author?.name || "Tiling Specialist"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Project Lead
                </p>
              </div>
              <CardContent className="p-6 pt-4">
                <p className="text-sm text-muted-foreground leading-relaxed text-center">
                  Specialist in large format porcelain, wet room tanking, and precision bathroom renovations with 11 years experience.
                </p>
                <Separator className="my-4" />
                <div className="flex justify-center gap-4">
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none hover:bg-secondary/10 hover:text-secondary">
                    <Share2 className="h-4 w-4" />
                    <span className="sr-only">Share</span>
                  </Button>
                  <Button asChild size="lg" className="w-full rounded-sm">
                    <Link to="#">
                      Get a Free Quote
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {}
            <div className="hidden lg:block">
               <Link to="#">
                <Button variant="ghost" className="w-full justify-start gap-2 text-muted-foreground hover:text-secondary rounded-sm">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Insights
                </Button>
               </Link>
            </div>
          </aside>

        </div>
      </div>
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-white min-h-screen text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}